import { useCallback, useEffect, useState } from 'react';
import type { SessionResponse } from '@movienight/shared';
import { DoorFlow } from './door/DoorFlow';
import { Home } from './home/Home';
import { doorLogout, getSession } from './lib/api';
import { navigate } from './lib/route';
import { SocketProvider } from './lib/SocketProvider';
import { TitleOrderProvider } from './lib/TitleOrderProvider';

/**
 * Phase 1 shell: the door, then the night sky. The showings list lands inside
 * Home once the importer exists — everything else there is already the real
 * page, not a placeholder.
 */
export default function App() {
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [checked, setChecked] = useState(false);
  const [entered, setEntered] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setSession(await getSession());
    } catch {
      setSession(null);
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The door renders itself as the loading state, so there is nothing to show
  // while the first session probe is in flight.
  if (!checked) return null;

  // Cold entry always goes through the door, even with a valid cookie — that
  // is the *only* place the "Šefe." nod can play, and skipping it whenever a
  // session existed made both `sefe` and `povratak` unreachable nodes.
  // `entered` is per page load, so in-app navigation never replays the scene.
  if (!entered) {
    return (
      <DoorFlow
        onEntered={() => {
          setEntered(true);
          void refresh();
        }}
      />
    );
  }

  if (!session?.user) {
    // The session died while they were inside — revoked, or expired. Send them
    // back to the door rather than showing a half-empty page.
    setEntered(false);
    return null;
  }

  // The socket only exists behind the door: it authenticates with the same
  // session cookie, and an upgrade without one is refused before a WebSocket
  // is created. Mounted here rather than around the whole app so logging out
  // tears the connection down as a matter of course.
  // Outside the socket provider on purpose: the language order is a property
  // of the page, not of the connection, and it has to survive a socket that
  // drops and remounts its children.
  return (
    <TitleOrderProvider initial={session.user.titleOrder}>
      <SocketProvider>
        <Home
          session={{ ...session, user: session.user }}
          onLogout={async () => {
            await doorLogout();
            // Whoever logs in next should meet the board, not whichever page the
            // last person happened to be on. The hash outlives the session.
            navigate('board');
            setEntered(false);
            await refresh();
          }}
        />
      </SocketProvider>
    </TitleOrderProvider>
  );
}
